/**
 * TBMuktBharat Campaign Detail Page
 * Displays campaign details with real API integration
 */

import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { GovLayout, GovCard, GovButton } from '@/components/layout/GovLayout'
import { Calendar, Users, MapPin, Target, Heart, Share2, ArrowLeft, Loader2 } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import campaignService, { Campaign } from '@/services/campaignService'

// Fallback mock data (used when API fails or for similar campaigns)
const mockCampaigns: Campaign[] = [
  {
    id: 1,
    title: 'TB-Free Village Initiative',
    description: 'Comprehensive TB screening and awareness campaign across 50 villages in Maharashtra. This initiative aims to provide free TB screening, awareness programs, and support for patients in rural areas.',
    type: 'SCREENING',
    status: 'ACTIVE',
    startDate: '2026-01-01',
    endDate: '2026-03-31',
    targetAmount: 500000,
    collectedAmount: 325000,
    targetParticipants: 10000,
    actualParticipants: 6500,
    location: 'Maharashtra',
    isFeatured: true
  },
  {
    id: 2,
    title: 'Awareness Week 2026',
    description: 'National TB awareness week with educational programs, free screenings, and community events across India.',
    type: 'AWARENESS',
    status: 'ACTIVE',
    startDate: '2026-02-01',
    endDate: '2026-02-07',
    targetParticipants: 50000,
    actualParticipants: 32000,
    location: 'Pan India',
    isFeatured: true
  },
  {
    id: 3,
    title: 'Health Camp for Workers',
    description: 'Free TB screening camp for factory and construction workers in Delhi NCR region.',
    type: 'HEALTH_CAMP',
    status: 'ACTIVE',
    startDate: '2026-01-15',
    endDate: '2026-02-15',
    targetAmount: 200000,
    collectedAmount: 180000,
    targetParticipants: 5000,
    actualParticipants: 4500,
    location: 'Delhi NCR',
    isFeatured: false
  },
  {
    id: 4,
    title: 'Support a TB Patient',
    description: 'Provide nutritional support and medicines for TB patients undergoing treatment across India.',
    type: 'SUPPORT',
    status: 'ACTIVE',
    startDate: '2026-01-01',
    endDate: '2026-12-31',
    targetAmount: 1000000,
    collectedAmount: 750000,
    targetParticipants: 1000,
    actualParticipants: 750,
    location: 'All India',
    isFeatured: false
  },
  {
    id: 5,
    title: 'Vaccination Drive',
    description: 'TB vaccination drive for high-risk populations in urban slums.',
    type: 'VACCINATION',
    status: 'ACTIVE',
    startDate: '2026-03-01',
    endDate: '2026-04-30',
    targetParticipants: 25000,
    actualParticipants: 18000,
    location: 'Mumbai, Pune, Nagpur',
    isFeatured: true
  },
  {
    id: 6,
    title: 'Community Outreach Program',
    description: 'Door-to-door awareness campaign in rural areas to identify undiagnosed TB cases.',
    type: 'COMMUNITY_OUTREACH',
    status: 'ACTIVE',
    startDate: '2026-02-15',
    endDate: '2026-05-15',
    targetParticipants: 15000,
    actualParticipants: 8500,
    location: 'Rajasthan',
    isFeatured: false
  }
]

const TYPE_COLORS: Record<string, string> = {
  AWARENESS: 'bg-blue-100 text-blue-700',
  SCREENING: 'bg-green-100 text-green-700',
  HEALTH_CAMP: 'bg-amber-100 text-amber-700',
  SUPPORT: 'bg-purple-100 text-purple-700',
  VACCINATION: 'bg-red-100 text-red-700',
  COMMUNITY_OUTREACH: 'bg-gray-100 text-gray-700',
  EDUCATION: 'bg-cyan-100 text-cyan-700'
}

const CampaignDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const { isAuthenticated } = useAuthStore()
  const [campaign, setCampaign] = useState<Campaign | null>(null)
  const [loading, setLoading] = useState(true)
  const [participating, setParticipating] = useState(false)
  const [liked, setLiked] = useState(false)

  // Fetch campaign details
  useEffect(() => {
    const fetchCampaign = async () => {
      setLoading(true)
      try {
        const data = await campaignService.getCampaignById(Number(id))
        setCampaign(data)
      } catch (error) {
        console.error('Failed to fetch campaign:', error)
        // Fallback to mock data
        const mockCampaign = mockCampaigns.find(c => c.id === Number(id))
        setCampaign(mockCampaign || null)
      } finally {
        setLoading(false)
      }
    }

    if (id) {
      fetchCampaign()
    }
  }, [id])

  const progress = campaign
    ? campaign.targetAmount
      ? Math.round(((campaign.collectedAmount || 0) / campaign.targetAmount) * 100)
      : campaign.targetParticipants
        ? Math.round(((campaign.actualParticipants || 0) / campaign.targetParticipants) * 100)
        : 0
    : 0

  const typeColor = campaign ? (TYPE_COLORS[campaign.type] || 'bg-gray-100 text-gray-700') : ''

  const handleParticipate = async () => {
    if (!isAuthenticated) {
      window.location.href = '/login'
      return
    }

    if (!campaign) return

    // Check if this is a mock campaign (IDs 1-6 are mock data)
    const isMockCampaign = campaign.id >= 1 && campaign.id <= 6 && !campaign.campaignUuid

    if (isMockCampaign) {
      // For mock campaigns, just toggle the state locally
      setCampaign(prev => prev ? { ...prev, isParticipating: !prev.isParticipating } : null)
      setParticipating(false)
      return
    }

    setParticipating(true)
    try {
      if (campaign.isParticipating) {
        const updated = await campaignService.leaveCampaign(campaign.id)
        setCampaign({ ...updated, isParticipating: false })
      } else {
        const updated = await campaignService.participate(campaign.id)
        setCampaign({ ...updated, isParticipating: true })
      }
    } catch (error) {
      console.error('Failed to participate:', error)
      alert('Failed to update participation. Please try again.')
    } finally {
      setParticipating(false)
    }
  }

  const handleShare = async () => {
    if (!campaign) return
    if (navigator.share) {
      await navigator.share({
        title: campaign.title,
        text: campaign.description,
        url: window.location.href
      })
    } else {
      navigator.clipboard.writeText(window.location.href)
      alert('Link copied to clipboard!')
    }
  }

  if (loading) {
    return (
      <GovLayout>
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      </GovLayout>
    )
  }

  if (!campaign) {
    return (
      <GovLayout>
        <GovCard className="text-center py-12">
          <Target className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Campaign Not Found</h2>
          <p className="text-gray-500 mb-4">The campaign you're looking for doesn't exist or has been removed.</p>
          <Link to="/campaigns">
            <GovButton>Back to Campaigns</GovButton>
          </Link>
        </GovCard>
      </GovLayout>
    )
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return ''
    return new Date(dateStr).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  // Similar campaigns (from mock data or same type)
  const similarCampaigns = mockCampaigns.filter(c => c.id !== campaign.id && c.type === campaign.type).slice(0, 3)

  return (
    <GovLayout>
      {/* Back Link */}
      <Link to="/campaigns" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4">
        <ArrowLeft className="w-4 h-4" />
        Back to Campaigns
      </Link>

      {/* Campaign Header */}
      <GovCard className="overflow-hidden p-0 mb-4">
        {/* Cover Image */}
        <div className="relative h-48 md:h-64 bg-gradient-to-br from-blue-500 to-cyan-500">
          <div className="absolute inset-0 flex items-center justify-center">
            <Target className="w-20 h-20 text-white/20" />
          </div>
          {campaign.isFeatured && (
            <span className="absolute top-4 left-4 px-3 py-1 text-sm font-semibold rounded-full bg-amber-500 text-white">
              Featured
            </span>
          )}
          <span className={`absolute top-4 right-4 px-3 py-1 text-sm font-medium rounded-full ${typeColor}`}>
            {campaign.type?.replace('_', ' ') || 'CAMPAIGN'}
          </span>
        </div>

        {/* Content */}
        <div className="p-4 md:p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{campaign.title}</h1>
          <p className="text-gray-600 mb-4">{campaign.description}</p>

          {/* Meta Info */}
          <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-6">
            {campaign.location && (
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {campaign.location}
              </span>
            )}
            {campaign.startDate && (
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(campaign.startDate)} - {formatDate(campaign.endDate)}
              </span>
            )}
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              campaign.status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
              campaign.status === 'COMPLETED' ? 'bg-blue-100 text-blue-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {campaign.status}
            </span>
          </div>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-500">Progress</span>
              <span className="font-bold text-green-600">{progress}%</span>
            </div>
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-green-500 to-blue-500 transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {campaign.targetAmount && (
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <div className="text-lg font-bold text-blue-600">₹{((campaign.collectedAmount || 0) / 100000).toFixed(1)}L</div>
                <div className="text-xs text-gray-500">Raised of ₹{(campaign.targetAmount / 100000).toFixed(1)}L</div>
              </div>
            )}
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-green-600">{(campaign.actualParticipants || 0).toLocaleString()}</div>
              <div className="text-xs text-gray-500">Participants</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-purple-600">{(campaign.targetParticipants || 0).toLocaleString()}</div>
              <div className="text-xs text-gray-500">Target</div>
            </div>
            <div className="bg-amber-50 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-amber-600">{campaign.type?.replace('_', ' ') || 'Campaign'}</div>
              <div className="text-xs text-gray-500">Type</div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleParticipate}
              disabled={participating}
              className={`flex-1 text-center px-4 py-2 font-medium rounded-lg transition-colors flex items-center justify-center gap-2 ${
                campaign.isParticipating
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              } disabled:opacity-50`}
            >
              {participating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Processing...
                </>
              ) : campaign.isParticipating ? (
                '✓ Participating'
              ) : (
                'Participate Now'
              )}
            </button>
            <button
              onClick={() => setLiked(!liked)}
              className={`p-2 rounded-lg transition-colors ${liked ? 'text-red-500 bg-red-50' : 'text-gray-400 hover:text-red-500 hover:bg-gray-100'}`}
            >
              <Heart className={`w-5 h-5 ${liked ? 'fill-current' : ''}`} />
            </button>
            <button
              onClick={handleShare}
              className="p-2 text-gray-400 hover:text-blue-500 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Share2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </GovCard>

      {/* Organizer Info */}
      {campaign.creator && (
        <GovCard className="mb-4">
          <h2 className="text-lg font-bold text-gray-900 mb-3">Organizer</h2>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="font-medium text-gray-900">
                {campaign.creator.firstName} {campaign.creator.lastName}
              </p>
              <p className="text-sm text-gray-500">@{campaign.creator.username}</p>
            </div>
          </div>
        </GovCard>
      )}

      {/* Similar Campaigns */}
      {similarCampaigns.length > 0 && (
        <>
          <h2 className="text-lg font-bold text-gray-900 mb-3">Similar Campaigns</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {similarCampaigns.map(c => (
              <Link key={c.id} to={`/campaigns/${c.id}`} className="block">
                <GovCard className="hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-900 mb-1">{c.title}</h3>
                  <p className="text-sm text-gray-500 line-clamp-2">{c.description}</p>
                </GovCard>
              </Link>
            ))}
          </div>
        </>
      )}
    </GovLayout>
  )
}

export default React.memo(CampaignDetailPage)